import type { ActualCollection, Collection } from './collection';
import { IndexIssue } from './indexAnalysisResults';
import type { ApplyIndexJob } from './job';
import { z } from 'zod';

export type IndexKey = z.infer<typeof IndexKey>;
export type IndexSpec = z.infer<typeof IndexSpec>;
export type IndexDefinition = z.infer<typeof IndexDefinition>;
export type ActualIndex = z.infer<typeof ActualIndex>;
export type Index = z.infer<typeof Index>;
export type IndexStats = z.infer<typeof IndexStats>;
export type IndexStatsSummary = z.infer<typeof IndexStatsSummary>;

export interface IndexTask {
  id: string;
  parentId?: string;
  collection?: Collection;
  index?: Index;
  toCreate?: ActualCollection[];
  toUpdate?: ActualIndex[];
  toDelete?: ActualIndex[];
  job?: ApplyIndexJob;
}

export const IndexKey = z.record(z.string(), z.union([z.number(), z.enum(['2d', '2dsphere', 'text', 'geoHaystack', 'hashed'])]));

export const IndexSpec = z.object({
  // Definition-only metadata: never sent to MongoDB and excluded from indexHash.
  description: z.string().optional(),
  name: z.string().optional(),
  key: IndexKey,
  partialFilterExpression: z.record(z.string(), z.unknown()).optional(),
  unique: z.boolean().optional(),
  sparse: z.boolean().optional(),
  expireAfterSeconds: z.number().optional(),
  hidden: z.boolean().optional(),
});

export const IndexDefinition = z.object({
  hash: z.string(),
  spec: IndexSpec,
});

export const IndexStats = z.object({
  accesses: z.object({
    ops: z.number(),
    since: z.coerce.date(),
  }),
  host: z.string(),
  building: z.boolean().optional(),
});

export const IndexStatsSummary = z.object({
  ops: z.number(),
  since: z.number(),
});

export const ActualIndex = z.object({
  db: z.string(),
  coll: z.string(),
  name: z.string(),
  hash: z.string(),
  spec: IndexSpec,
  size: z.number(),
  cacheSize: z.number().optional(),
  cachePageReads: z.number().optional(),
  stats: IndexStats.array().optional(),
  statsSummary: IndexStatsSummary.optional(),
});

export const Index = z.object({
  db: z.string(),
  coll: z.string(),
  name: z.string(),
  isDefined: z.boolean(),
  /** Created by the server itself (`_id_`, a timeseries collection's meta/time index), so it is never created or dropped. */
  implicit: z.boolean().optional(),
  hash: z.string(),
  spec: IndexSpec,
  actual: ActualIndex.array(),
  statsSummary: IndexStatsSummary,
  issues: z.array(IndexIssue),
});
